import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';
import {
  MapPin, Clock, Banknote, Coffee, Calendar as CalendarIcon,
  Users, ChevronRight, Pencil, Eye, FileText, Loader2, Search, X, Trash2, Star,
  Ban, UserX, Copy, AlertTriangle, ArchiveIcon, CreditCard, CheckCircle2, ArrowLeft, ChevronDown, CalendarRange
} from 'lucide-react';
import { toast } from 'sonner';
import { getCoverageDisplay } from '../../utils/coverageDisplay';
import { getShiftStatus } from '../../lib/shiftStatus';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Shifts' },
  { value: 'open', label: 'Open' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no_show', label: 'No Show' },
  { value: 'disputed', label: 'Disputed' },
];

const ACTIVE_STATUSES = ['open', 'assigned', 'in_progress'];
const HISTORY_STATUSES = ['completed', 'cancelled', 'no_show', 'disputed'];

const statusStyles = {
  open: 'bg-blue-100 text-blue-800',
  assigned: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-emerald-100 text-emerald-800',
  completed: 'bg-slate-100 text-slate-700',
  disputed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-600',
  no_show: 'bg-red-100 text-red-800',
};

export default function ManagerMyShifts() {
  const { api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState(searchParams.get('tab') === 'history' ? 'history' : 'active');
  const [activeOpen, setActiveOpen] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(searchParams.get('tab') === 'history');

  const [editShift, setEditShift] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editDate, setEditDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [confirmNoShow, setConfirmNoShow] = useState(null);
  const [markingNoShow, setMarkingNoShow] = useState(null);
  const [repostShift, setRepostShift] = useState(null);
  const [repostForm, setRepostForm] = useState({});
  const [repostDate, setRepostDate] = useState(null);
  const [reposting, setReposting] = useState(false);

  const [ratingShift, setRatingShift] = useState(null);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [payingShift, setPayingShift] = useState(null);
  const debounceRef = useRef(null);

  const fetchShifts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      const url = params.toString() ? `/shifts?${params}` : '/shifts';
      const response = await api.get(url);
      setShifts(response.data);
    } catch (error) {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter, searchQuery]);

  useEffect(() => {
    setLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchShifts();
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [fetchShifts]);

  const filtered = shifts.filter(s => {
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      if (!s.branchName?.toLowerCase().includes(q) && !s.postcode?.toLowerCase().includes(q) && !s.location?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const activeShifts = filtered.filter(s => ACTIVE_STATUSES.includes(s.status));
  const historyShifts = filtered.filter(s => HISTORY_STATUSES.includes(s.status));
  const activeCount = activeShifts.length;
  const historyCount = historyShifts.length;

  const openEditModal = (shift) => {
    setEditShift(shift);
    setEditForm({
      branchName: shift.branchName,
      location: shift.location,
      postcode: shift.postcode || '',
      city: shift.city || '',
      area: shift.area || '',
      scheduledStartTime: shift.scheduledStartTime,
      scheduledEndTime: shift.scheduledEndTime,
      breakDurationMinutes: shift.breakDurationMinutes,
      pay: shift.pay,
      notes: shift.notes || '',
    });
    setEditDate(new Date(shift.date + 'T00:00:00'));
  };

  const handleSaveEdit = async () => {
    if (!editShift) return;
    setSaving(true);
    try {
      await api.put(`/shifts/${editShift.id}`, {
        ...editForm,
        date: editDate ? format(editDate, 'yyyy-MM-dd') : editShift.date,
        pay: parseFloat(editForm.pay),
        breakDurationMinutes: parseInt(editForm.breakDurationMinutes) || 0,
      });
      toast.success('Shift updated');
      setEditShift(null);
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update shift');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (shiftId) => {
    setDeleting(shiftId);
    setConfirmDelete(null);
    try {
      await api.delete(`/shifts/${shiftId}`);
      toast.success('Shift deleted');
      setShifts(prev => prev.filter(s => s.id !== shiftId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete shift');
    } finally {
      setDeleting(null);
    }
  };

  const handleCancel = async (shiftId) => {
    setCancelling(shiftId);
    setConfirmCancel(null);
    try {
      await api.post(`/shifts/${shiftId}/cancel`);
      toast.success('Shift cancelled');
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel shift');
    } finally {
      setCancelling(null);
    }
  };

  const handleNoShow = async (shiftId) => {
    setMarkingNoShow(shiftId);
    setConfirmNoShow(null);
    try {
      const res = await api.post(`/shifts/${shiftId}/no-show`);
      toast.success(`Worker marked as no-show. £${res.data.penalty} penalty applied.`);
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark no-show');
    } finally {
      setMarkingNoShow(null);
    }
  };

  const openRepostModal = (shift) => {
    setRepostShift(shift);
    setRepostForm({
      branchName: shift.branchName,
      location: shift.location,
      scheduledStartTime: shift.scheduledStartTime,
      scheduledEndTime: shift.scheduledEndTime,
      breakDurationMinutes: shift.breakDurationMinutes,
      pay: shift.pay,
      notes: shift.notes || '',
    });
    setRepostDate(new Date(shift.date + 'T00:00:00'));
  };

  const handleRepost = async () => {
    if (!repostShift) return;
    setReposting(true);
    try {
      // Create repost from original
      const res = await api.post(`/shifts/${repostShift.id}/repost`);
      const newId = res.data.id;
      // Now update the new shift with edited values
      await api.put(`/shifts/${newId}`, {
        ...repostForm,
        date: repostDate ? format(repostDate, 'yyyy-MM-dd') : repostShift.date,
        pay: parseFloat(repostForm.pay),
        breakDurationMinutes: parseInt(repostForm.breakDurationMinutes) || 0,
      });
      toast.success('Shift reposted successfully');
      setRepostShift(null);
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to repost shift');
    } finally {
      setReposting(false);
    }
  };

  const openRatingModal = (shift) => {
    setRatingShift(shift);
    setRatingValue(0);
    setRatingComment('');
  };

  const handleSubmitRating = async () => {
    if (!ratingShift || ratingValue < 1) {
      toast.error('Please select a rating');
      return;
    }
    if (!ratingComment.trim()) {
      toast.error('Written review is required');
      return;
    }
    setRatingSubmitting(true);
    try {
      await api.post('/ratings', {
        shiftId: ratingShift.id,
        workerId: ratingShift.assignedWorkerId,
        ratingValue,
        comment: ratingComment.trim()
      });
      toast.success('Review submitted');
      setRatingShift(null);
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setRatingSubmitting(false);
    }
  };

  const handlePayShift = async (shiftId) => {
    setPayingShift(shiftId);
    try {
      // Try off-session retry charge first (uses saved card)
      const res = await api.post(`/payments/retry-shift-charge/${shiftId}`);
      const result = res.data?.result;
      if (result?.status === 'succeeded') {
        toast.success(`Charged £${Number(result.amount).toFixed(2)} successfully`);
        fetchShifts();
        if (refreshUser) refreshUser();
      } else if (result?.status === 'failed') {
        const reason = result.reason || 'unknown';
        if (reason === 'no_payment_method') {
          // No card on file → start setup card flow
          const setupRes = await api.post('/payments/setup-card', { originUrl: window.location.origin });
          if (setupRes.data?.url) {
            window.location.href = setupRes.data.url;
            return;
          }
        }
        toast.error(`Charge failed: ${result.error || reason}. Update your card from Billing.`);
      } else if (result?.status === 'covered_by_subscription') {
        toast.success('Shift is covered by your subscription.');
        fetchShifts();
      } else {
        toast.message('Charge attempt completed.');
        fetchShifts();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to retry charge');
      toast.error(msg);
    } finally {
      setPayingShift(null);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const renderShiftCard = (shift) => (
    <Card key={shift.id} className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`manager-shift-${shift.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-semibold text-slate-900 text-lg">{shift.branchName}</h3>
            <div className="flex items-center gap-1 text-slate-500 mt-1">
              <MapPin className="h-4 w-4 shrink-0" />
              <span className="text-sm truncate">{shift.location}</span>
            </div>
            {shift.postcode && (
              <Badge variant="outline" className="mt-1.5 text-xs font-mono font-medium text-slate-600 border-slate-200 bg-slate-50 px-2 py-0.5 w-fit">
                {shift.postcode}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Badge className={statusStyles[shift.status] || 'bg-slate-100'} data-testid={`shift-status-${shift.id}`}>{getShiftStatus(shift).label}</Badge>
            {shift.needsReview && <Badge className="bg-amber-100 text-amber-800 text-xs" data-testid={`needs-review-badge-${shift.id}`}>Review Required</Badge>}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm text-slate-600 mb-4">
          <div className="flex items-center gap-1.5"><CalendarIcon className="h-4 w-4 text-slate-400 shrink-0" /><span>{formatDate(shift.date)}</span></div>
          <div className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-slate-400 shrink-0" /><span>{shift.scheduledStartTime} - {shift.scheduledEndTime}</span></div>
          <div className="flex items-center gap-1.5"><Coffee className="h-4 w-4 text-slate-400 shrink-0" /><span>{shift.breakDurationMinutes}m break</span></div>
          <div className="flex items-center gap-1.5"><Banknote className="h-4 w-4 text-slate-400 shrink-0" /><span className="font-medium">&pound;{shift.pay?.toFixed(2)}</span></div>
        </div>
        {/* Coverage days badge */}
        {(() => {
          const cov = getCoverageDisplay(shift);
          if (!cov) return null;
          return (
            <div className="mb-3 flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg w-fit" data-testid={`coverage-badge-${shift.id}`}>
              <CalendarRange className="h-3.5 w-3.5 text-blue-600 shrink-0" />
              <span className="text-xs font-medium text-blue-800">{cov.full}</span>
            </div>
          );
        })()}
        {/* Assigned worker display */}
        <div className="mb-4">
          {shift.assignedWorkerName ? (
            <div>
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-200" data-testid={`assigned-worker-${shift.id}`}>
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#0f1b3d] to-[#2a3f6d] flex items-center justify-center text-white text-sm font-bold shrink-0">
                  {shift.assignedWorkerName.charAt(0)}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-emerald-900">{shift.assignedWorkerName}</p>
                  <p className="text-xs text-emerald-700">Assigned worker</p>
                </div>
                <Badge className="bg-emerald-100 text-emerald-800">Assigned</Badge>
              </div>
              {shift.backupCount > 0 && (
                <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200" data-testid={`backup-info-${shift.id}`}>
                  <Users className="h-3.5 w-3.5 text-amber-600" />
                  <span className="text-xs text-amber-700 font-medium">{shift.backupCount} backup applicant{shift.backupCount !== 1 ? 's' : ''} available</span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-slate-500 p-3 bg-slate-50 rounded-xl" data-testid={`no-worker-${shift.id}`}>
              <Users className="h-4 w-4 text-slate-400" />
              <span>No worker assigned yet</span>
              {shift.applicantCount > 0 && (
                <Badge className="bg-blue-100 text-blue-800 ml-auto">{shift.applicantCount} worker{shift.applicantCount !== 1 ? 's' : ''} available</Badge>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
          {shift.status === 'open' && shift.applicantCount > 0 && <Button size="sm" variant="outline" className="rounded-full text-sm h-11" onClick={() => navigate(`/manager/applicants?shiftId=${shift.id}`)} data-testid={`view-applicants-btn-${shift.id}`}><Users className="h-3.5 w-3.5 mr-1.5" /> Review workers ({shift.applicantCount})</Button>}
          {shift.status === 'assigned' && shift.backupCount > 0 && <Button size="sm" variant="outline" className="rounded-full text-sm h-11 text-amber-700 border-amber-200 hover:bg-amber-50" onClick={() => navigate(`/manager/applicants?shiftId=${shift.id}`)} data-testid={`review-backups-btn-${shift.id}`}><Users className="h-3.5 w-3.5 mr-1.5" /> Review backup workers ({shift.backupCount})</Button>}
          {['open', 'assigned'].includes(shift.status) && <Button size="sm" variant="outline" className="rounded-full text-sm h-11" onClick={() => openEditModal(shift)} data-testid={`edit-shift-btn-${shift.id}`}><Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit</Button>}
          <Button size="sm" variant="outline" className="rounded-full text-sm h-11" onClick={() => navigate(`/manager/logs/${shift.id}`)} data-testid={`view-logs-btn-${shift.id}`}><FileText className="h-3.5 w-3.5 mr-1.5" /> Logs</Button>
          {shift.shiftPaymentStatus === 'pending' && shift.status !== 'completed' && (
            <Badge className="bg-blue-100 text-blue-800 text-xs h-9 px-3 flex items-center" data-testid={`autocharge-pending-badge-${shift.id}`}>
              <CreditCard className="h-3.5 w-3.5 mr-1.5" /> Payment on completion
            </Badge>
          )}
          {shift.status === 'completed' && shift.shiftPaymentStatus === 'paid' && (
            <Badge className="bg-emerald-100 text-emerald-800 text-xs h-9 px-3 flex items-center" data-testid={`autocharge-paid-badge-${shift.id}`}>
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Paid{shift.amountCharged ? ` £${Number(shift.amountCharged).toFixed(2)}` : ''}
            </Badge>
          )}
          {shift.status === 'completed' && shift.shiftPaymentStatus === 'covered_by_subscription' && (
            <Badge className="bg-slate-100 text-slate-700 text-xs h-9 px-3 flex items-center" data-testid={`autocharge-covered-badge-${shift.id}`}>
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Covered by subscription
            </Badge>
          )}
          {shift.status === 'completed' && shift.shiftPaymentStatus === 'payment_failed' && (
            <Button
              size="sm"
              className="rounded-full text-sm h-11 bg-red-600 hover:bg-red-700 text-white"
              onClick={() => handlePayShift(shift.id)}
              disabled={payingShift === shift.id}
              data-testid={`retry-charge-btn-${shift.id}`}
            >
              {payingShift === shift.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />} Payment Failed — Retry
            </Button>
          )}
          {shift.status === 'completed' && shift.assignedWorkerId && !shift.ratingId && <Button size="sm" className="rounded-full text-sm h-11 bg-amber-500 hover:bg-amber-600 text-white" onClick={() => openRatingModal(shift)} data-testid={`rate-worker-btn-${shift.id}`}><Star className="h-3.5 w-3.5 mr-1.5" /> Review Required</Button>}
          {shift.status === 'completed' && shift.assignedWorkerId && shift.ratingId && <Badge className="bg-emerald-100 text-emerald-800 text-xs h-9 px-3 flex items-center" data-testid={`reviewed-badge-${shift.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Reviewed</Badge>}
          {['open', 'assigned', 'in_progress'].includes(shift.status) && <Button size="sm" variant="outline" className="rounded-full text-sm h-11 text-orange-600 hover:text-orange-700 hover:bg-orange-50" onClick={() => setConfirmCancel(shift.id)} disabled={cancelling === shift.id} data-testid={`cancel-shift-btn-${shift.id}`}>{cancelling === shift.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Ban className="h-3.5 w-3.5 mr-1.5" />} Cancel</Button>}
          {['assigned', 'in_progress'].includes(shift.status) && shift.assignedWorkerId && <Button size="sm" variant="outline" className="rounded-full text-sm h-11 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setConfirmNoShow(shift.id)} disabled={markingNoShow === shift.id} data-testid={`no-show-btn-${shift.id}`}>{markingNoShow === shift.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <UserX className="h-3.5 w-3.5 mr-1.5" />} No-Show</Button>}
          {['cancelled', 'no_show'].includes(shift.status) && <Button size="sm" variant="outline" className="rounded-full text-sm h-11 text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => openRepostModal(shift)} data-testid={`repost-shift-btn-${shift.id}`}><Copy className="h-3.5 w-3.5 mr-1.5" /> Repost</Button>}
          {!['in_progress', 'disputed'].includes(shift.status) && <Button size="sm" variant="outline" className="rounded-full text-sm h-11 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setConfirmDelete(shift.id)} disabled={deleting === shift.id} data-testid={`delete-shift-btn-${shift.id}`}>{deleting === shift.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />} Delete</Button>}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-12 w-full" />
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-44" />)}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="manager-my-shifts-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-full h-11 w-11 min-w-[44px] shrink-0" onClick={() => navigate('/manager')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']">My Shifts</h1>
          <p className="text-slate-500 mt-1">Manage active shifts and review history</p>
        </div>
      </div>

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by postcode or branch name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10 h-12 rounded-xl bg-slate-50 border-slate-200"
            data-testid="shift-search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="h-4 w-4 text-slate-400" />
            </button>
          )}
        </div>
      </div>

      {/* Active Shifts - Collapsible */}
      <Collapsible open={activeOpen} onOpenChange={setActiveOpen}>
        <CollapsibleTrigger asChild>
          <button className="flex items-center justify-between w-full p-4 bg-white rounded-xl shadow-sm hover:bg-slate-50 transition-colors" data-testid="active-section-toggle">
            <div className="flex items-center gap-3">
              <div className="h-3 w-3 rounded-full bg-emerald-500"></div>
              <h2 className="text-lg font-semibold text-slate-900">Active Shifts</h2>
              <Badge className="bg-emerald-100 text-emerald-800">{activeCount}</Badge>
            </div>
            <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${activeOpen ? 'rotate-180' : ''}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-4 mt-3">
            {activeShifts.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-8 text-center">
                  <CalendarIcon className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-medium text-slate-900 mb-1">No Active Shifts</h3>
                  <p className="text-sm text-slate-500 mb-4">All quiet right now.</p>
                  <Button onClick={() => navigate('/manager/post-shift')} className="rounded-full bg-[#DA291C] hover:bg-[#b91f13]" data-testid="create-shift-btn">
                    Post New Shift
                  </Button>
                </CardContent>
              </Card>
            ) : activeShifts.map(shift => renderShiftCard(shift))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* History - Collapsible */}
      <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
        <CollapsibleTrigger asChild>
          <button className="flex items-center justify-between w-full p-4 bg-white rounded-xl shadow-sm hover:bg-slate-50 transition-colors" data-testid="history-section-toggle">
            <div className="flex items-center gap-3">
              <div className="h-3 w-3 rounded-full bg-slate-400"></div>
              <h2 className="text-lg font-semibold text-slate-900">History</h2>
              <Badge className="bg-slate-100 text-slate-700">{historyCount}</Badge>
            </div>
            <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${historyOpen ? 'rotate-180' : ''}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-4 mt-3">
            {historyShifts.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-8 text-center">
                  <ArchiveIcon className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-medium text-slate-900 mb-1">No History Yet</h3>
                  <p className="text-sm text-slate-500">Completed shifts will appear here.</p>
                </CardContent>
              </Card>
            ) : historyShifts.map(shift => renderShiftCard(shift))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Edit Shift Dialog */}
      <Dialog open={!!editShift} onOpenChange={(open) => !open && setEditShift(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Shift</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Branch Name</Label>
              <Input value={editForm.branchName || ''} onChange={e => setEditForm(p => ({ ...p, branchName: e.target.value }))} className="h-11 rounded-lg" data-testid="edit-branch-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Location</Label>
              <Input value={editForm.location || ''} onChange={e => setEditForm(p => ({ ...p, location: e.target.value }))} className="h-11 rounded-lg" data-testid="edit-location" />
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full h-11 justify-start text-left", !editDate && "text-muted-foreground")} data-testid="edit-date-picker">
                    {editDate ? format(editDate, "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={editDate} onSelect={setEditDate} /></PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Start Time</Label>
                <Input type="time" value={editForm.scheduledStartTime || ''} onChange={e => setEditForm(p => ({ ...p, scheduledStartTime: e.target.value }))} className="h-11 rounded-lg" data-testid="edit-start-time" />
              </div>
              <div className="space-y-1.5">
                <Label>End Time</Label>
                <Input type="time" value={editForm.scheduledEndTime || ''} onChange={e => setEditForm(p => ({ ...p, scheduledEndTime: e.target.value }))} className="h-11 rounded-lg" data-testid="edit-end-time" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Break (min)</Label>
                <Input type="number" min="0" value={editForm.breakDurationMinutes ?? ''} onChange={e => setEditForm(p => ({ ...p, breakDurationMinutes: e.target.value }))} className="h-11 rounded-lg" data-testid="edit-break" />
              </div>
              <div className="space-y-1.5">
                <Label>Pay (&pound;)</Label>
                <Input type="number" min="0" step="0.01" value={editForm.pay ?? ''} onChange={e => setEditForm(p => ({ ...p, pay: e.target.value }))} className="h-11 rounded-lg" data-testid="edit-pay" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={editForm.notes || ''} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="rounded-lg resize-none" data-testid="edit-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditShift(null)} className="rounded-full">Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="rounded-full bg-[#0f1b3d] hover:bg-[#1a2d5a]" data-testid="save-edit-btn">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Delete this shift?</DialogTitle></DialogHeader>
          <p className="text-slate-600">The shift and all availability requests will be removed. Workers who applied will be told the shift is no longer available. This can&apos;t be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)} className="rounded-full min-h-[48px]">Keep the shift</Button>
            <Button onClick={() => handleDelete(confirmDelete)} className="rounded-full bg-red-600 hover:bg-red-700 text-white min-h-[48px]" data-testid="confirm-delete-btn">Yes, delete shift</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation */}
      <Dialog open={!!confirmCancel} onOpenChange={(open) => !open && setConfirmCancel(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Cancel this shift?</DialogTitle></DialogHeader>
          <p className="text-slate-600">We&apos;ll let all applicants and the assigned worker know straight away. You can post a new one any time.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmCancel(null)} className="rounded-full min-h-[48px]">Keep the shift</Button>
            <Button onClick={() => handleCancel(confirmCancel)} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white min-h-[48px]" data-testid="confirm-cancel-btn">Yes, cancel shift</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* No-Show Confirmation */}
      <Dialog open={!!confirmNoShow} onOpenChange={(open) => !open && setConfirmNoShow(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Mark No-Show</DialogTitle></DialogHeader>
          <p className="text-slate-600">This will mark the assigned worker as a no-show. A <strong>&pound;50 penalty</strong> will be applied and their reliability score will decrease.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmNoShow(null)} className="rounded-full">Go Back</Button>
            <Button onClick={() => handleNoShow(confirmNoShow)} className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-no-show-btn">Confirm No-Show</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rating/Review Dialog */}
      <Dialog open={!!ratingShift} onOpenChange={(open) => !open && setRatingShift(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Review Worker Performance</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-slate-600">Rate and review {ratingShift?.assignedWorkerName} for their performance at {ratingShift?.branchName}.</p>
            <div className="flex justify-center gap-2 py-2">
              {[1, 2, 3, 4, 5].map(i => (
                <button key={i} onClick={() => setRatingValue(i)} className="transition-transform hover:scale-110" data-testid={`star-${i}`}>
                  <Star className={`h-10 w-10 ${i <= ratingValue ? 'text-amber-400 fill-current' : 'text-slate-300'}`} />
                </button>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label>Written Review <span className="text-red-500">*</span></Label>
              <Textarea value={ratingComment} onChange={e => setRatingComment(e.target.value)} placeholder="How was the worker's performance? This review is required." rows={3} className="rounded-lg resize-none" data-testid="rating-comment" />
              {ratingComment.trim() === '' && ratingValue > 0 && (
                <p className="text-xs text-red-500">A written review is required to submit</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRatingShift(null)} className="rounded-full">Cancel</Button>
            <Button onClick={handleSubmitRating} disabled={ratingSubmitting || ratingValue < 1 || !ratingComment.trim()} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="submit-rating-btn">
              {ratingSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Star className="h-4 w-4 mr-2" />}
              Submit Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repost Shift Dialog */}
      <Dialog open={!!repostShift} onOpenChange={(open) => !open && setRepostShift(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Repost Shift</DialogTitle>
            <p className="text-sm text-slate-500 mt-1">Edit the details before reposting. A new shift will be created.</p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Branch Name</Label>
              <Input value={repostForm.branchName || ''} onChange={e => setRepostForm(p => ({ ...p, branchName: e.target.value }))} className="h-11 rounded-lg" data-testid="repost-branch-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Location</Label>
              <Input value={repostForm.location || ''} onChange={e => setRepostForm(p => ({ ...p, location: e.target.value }))} className="h-11 rounded-lg" data-testid="repost-location" />
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full h-11 justify-start text-left", !repostDate && "text-muted-foreground")} data-testid="repost-date-picker">
                    {repostDate ? format(repostDate, "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={repostDate} onSelect={setRepostDate} /></PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Start Time</Label>
                <Input type="time" value={repostForm.scheduledStartTime || ''} onChange={e => setRepostForm(p => ({ ...p, scheduledStartTime: e.target.value }))} className="h-11 rounded-lg" data-testid="repost-start-time" />
              </div>
              <div className="space-y-1.5">
                <Label>End Time</Label>
                <Input type="time" value={repostForm.scheduledEndTime || ''} onChange={e => setRepostForm(p => ({ ...p, scheduledEndTime: e.target.value }))} className="h-11 rounded-lg" data-testid="repost-end-time" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Break (min)</Label>
                <Input type="number" min="0" value={repostForm.breakDurationMinutes ?? ''} onChange={e => setRepostForm(p => ({ ...p, breakDurationMinutes: e.target.value }))} className="h-11 rounded-lg" data-testid="repost-break" />
              </div>
              <div className="space-y-1.5">
                <Label>Pay (&pound;)</Label>
                <Input type="number" min="0" step="0.01" value={repostForm.pay ?? ''} onChange={e => setRepostForm(p => ({ ...p, pay: e.target.value }))} className="h-11 rounded-lg" data-testid="repost-pay" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={repostForm.notes || ''} onChange={e => setRepostForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="rounded-lg resize-none" data-testid="repost-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRepostShift(null)} className="rounded-full">Cancel</Button>
            <Button onClick={handleRepost} disabled={reposting} className="rounded-full bg-[#0f1b3d] hover:bg-[#1a2d5a]" data-testid="confirm-repost-btn">
              {reposting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reposting...</> : <><Copy className="h-4 w-4 mr-2" /> Repost Shift</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
